import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import BudgetPanel from '../components/expenses/BudgetPanel';
import api from '../services/api';
import { formatDay, formatMoney } from '../components/commercial/contact';

type Material = { id: number; name: string; unit?: string; cost: number; costUsd?: number | null };

/** Dólar para costos, objetivos del mes, tope de gastos y presupuesto por categoría. */
const CommercialSettingsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState({ usdRate: 0, monthlySalesGoal: 0, monthlyUnitsGoal: 0, expenseRatioLimit: 0.5 });
  const [rateDate, setRateDate] = useState<string | null>(null);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    const [s, c] = await Promise.all([api.getCommercialSettings(), api.getExpenseCategories()]);
    setSettings({ usdRate: s.usdRate, monthlySalesGoal: s.monthlySalesGoal, monthlyUnitsGoal: s.monthlyUnitsGoal, expenseRatioLimit: s.expenseRatioLimit });
    setRateDate(s.usdRateUpdatedAt);
    setMaterials(s.materials);
    setCategories(c);
  }, []);

  useEffect(() => { load().catch(() => setMsg({ type: 'error', text: 'No se pudo cargar la configuración' })); }, [load]);

  const saveSettings = async () => {
    try {
      const r = await api.saveCommercialSettings(settings);
      await api.setMaterialsUsd(materials.map(m => ({ materialId: m.id, costUsd: m.costUsd ? Number(m.costUsd) : null })));
      setMsg({ type: 'success', text: r.updatedMaterials ? `Guardado. Se recalculó el costo de ${r.updatedMaterials} materias primas con el dólar nuevo.` : 'Guardado' });
      load();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.message || 'No se pudo guardar' });
    }
  };

  // Completa un objetivo a partir del otro con el precio promedio por unidad del mes pasado.
  // Solo llena el campo: se puede editar y se guarda con el botón Guardar de siempre.
  const [goalHint, setGoalHint] = useState<string | null>(null);
  const suggestGoal = async (from: 'sales' | 'units') => {
    try {
      const r = await api.getGoalSuggestion(from === 'sales' ? { salesGoal: settings.monthlySalesGoal } : { unitsGoal: settings.monthlyUnitsGoal });
      if (r.averageUnitPrice == null) {
        setGoalHint(`No hubo ventas en ${r.month} para sacar el precio promedio por unidad.`);
        return;
      }
      if (from === 'sales' && r.suggestedUnitsGoal != null) setSettings(s => ({ ...s, monthlyUnitsGoal: r.suggestedUnitsGoal! }));
      if (from === 'units' && r.suggestedSalesGoal != null) setSettings(s => ({ ...s, monthlySalesGoal: r.suggestedSalesGoal! }));
      setGoalHint(`Calculado con el precio promedio por unidad de ${r.month}: ${formatMoney(r.averageUnitPrice)} (${formatMoney(r.revenue)} en ${r.units} unidades). Podés editarlo y después tocá Guardar.`);
    } catch {
      setGoalHint('No se pudo calcular el objetivo');
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>Costos, objetivos y presupuesto</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Presupuesto de gastos" />
        <Tab label="Dólar y objetivos" />
      </Tabs>
      {msg && <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ mb: 2 }}>{msg.text}</Alert>}

      {tab === 0 && (
        <Stack spacing={2}>
          <Alert severity="info">
            El presupuesto y los gastos también se cargan desde <Link component={RouterLink} to="/expenses?tab=presupuesto">Gastos → Presupuesto</Link>.
          </Alert>
          <BudgetPanel categories={categories} />
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth type="number" label="Cotización dólar" value={settings.usdRate}
                    onChange={e => setSettings({ ...settings, usdRate: Number(e.target.value) })}
                    helperText={rateDate ? `Actualizada ${formatDay(rateDate)}` : 'Para materias primas en USD'} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth type="number" label="Objetivo de ventas del mes ($)" value={settings.monthlySalesGoal}
                    onChange={e => setSettings({ ...settings, monthlySalesGoal: Number(e.target.value) })} />
                  <Button size="small" sx={{ mt: 0.5 }} disabled={!settings.monthlySalesGoal} onClick={() => suggestGoal('sales')}>
                    Calcular unidades
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth type="number" label="Objetivo de unidades del mes" value={settings.monthlyUnitsGoal}
                    onChange={e => setSettings({ ...settings, monthlyUnitsGoal: Number(e.target.value) })} />
                  <Button size="small" sx={{ mt: 0.5 }} disabled={!settings.monthlyUnitsGoal} onClick={() => suggestGoal('units')}>
                    Calcular facturación
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth type="number" label="Tope de gastos (% de facturación)" value={Math.round(settings.expenseRatioLimit * 100)}
                    onChange={e => setSettings({ ...settings, expenseRatioLimit: Number(e.target.value) / 100 })} />
                </Grid>
              </Grid>
              {goalHint && <Alert severity="info" sx={{ mt: 2 }} onClose={() => setGoalHint(null)}>{goalHint}</Alert>}
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight={700}>Materias primas en dólares</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Cargá el costo en USD de lo que comprás en dólares (pomo, etiqueta, caja…). Al cambiar la cotización, el costo en pesos y el de las recetas se recalculan solos.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Materia prima</TableCell>
                    <TableCell align="right">Costo USD</TableCell>
                    <TableCell align="right">Costo $</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {materials.map(m => (
                    <TableRow key={m.id}>
                      <TableCell>{m.name}{m.unit ? ` (${m.unit})` : ''}</TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" value={m.costUsd ?? ''} sx={{ width: 120 }}
                          onChange={e => setMaterials(x => x.map(y => (y.id === m.id ? { ...y, costUsd: e.target.value === '' ? null : Number(e.target.value) } : y)))} />
                      </TableCell>
                      <TableCell align="right">{formatMoney(m.costUsd && settings.usdRate ? m.costUsd * settings.usdRate : m.cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <Box><Button variant="contained" startIcon={<Save />} onClick={saveSettings}>Guardar</Button></Box>
        </Stack>
      )}
    </Box>
  );
};

export default CommercialSettingsPage;
